/**
 * Card asset lifecycle — the server side of the asynchronous imaging pipeline.
 *
 * A card asset is one uploaded photo tracked through
 * `uploading → processing → ready` (or `failed`). The listing stores a stable
 * URL that points at the asset, not at a storage object, so the same URL can
 * serve the seller's raw upload while processing runs and the finished studio
 * render the moment it lands. See `src/routes/api/public/card-image.$assetId.ts`.
 */

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safePath(value: unknown, field: string): string {
  if (typeof value !== "string" || !value || value.includes("..") || value.startsWith("/")) {
    throw new Error(`invalid ${field}`);
  }
  return value;
}

function requireId(value: unknown): string {
  if (typeof value !== "string" || !UUID.test(value)) throw new Error("invalid asset id");
  return value;
}

export type CardAssetStatus = "uploading" | "processing" | "ready" | "failed";

/** Register a freshly uploaded photo and move it into the processing queue. */
export const createCardAsset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: {
    id: string;
    originalPath: string;
    sourcePath: string;
    mime?: string;
    width?: number;
    height?: number;
  }) => ({
    id: requireId(input?.id),
    originalPath: safePath(input?.originalPath, "originalPath"),
    sourcePath: safePath(input?.sourcePath, "sourcePath"),
    mime: typeof input?.mime === "string" ? input.mime.slice(0, 60) : null,
    width: Number.isFinite(input?.width) ? Math.round(input!.width!) : null,
    height: Number.isFinite(input?.height) ? Math.round(input!.height!) : null,
  }))
  .handler(async ({ data, context }) => {
    for (const p of [data.originalPath, data.sourcePath]) {
      if (p.split("/")[0] !== context.userId) throw new Error("Forbidden");
    }
    const { error } = await context.supabase.from("card_assets").insert({
      id: data.id,
      owner_id: context.userId,
      status: "processing",
      original_path: data.originalPath,
      source_path: data.sourcePath,
      mime: data.mime,
      width: data.width,
      height: data.height,
    });
    if (error) throw error;
    return { id: data.id, status: "processing" as CardAssetStatus };
  });

/** Mark the derivative ladder as published and flip the asset to `ready`. */
export const completeCardAsset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: {
    id: string;
    deepzoomPath: string;
    ultraPath: string;
    previewPath: string;
    thumbPath: string;
    sourcePath?: string;
    mime: string;
    meta?: Record<string, unknown>;
  }) => ({
    id: requireId(input?.id),
    deepzoomPath: safePath(input?.deepzoomPath, "deepzoomPath"),
    ultraPath: safePath(input?.ultraPath, "ultraPath"),
    previewPath: safePath(input?.previewPath, "previewPath"),
    thumbPath: safePath(input?.thumbPath, "thumbPath"),
    sourcePath: input?.sourcePath ? safePath(input.sourcePath, "sourcePath") : null,
    mime: typeof input?.mime === "string" ? input.mime.slice(0, 60) : "image/webp",
    meta: input?.meta && typeof input.meta === "object" ? input.meta : {},
  }))
  .handler(async ({ data, context }) => {
    // Derivatives must live under the caller's own prefix — otherwise the
    // public image route could be pointed at another seller's objects.
    for (const p of [data.deepzoomPath, data.ultraPath, data.previewPath, data.thumbPath, data.sourcePath].filter((value): value is string => Boolean(value))) {
      if (p.split("/")[0] !== context.userId) throw new Error("Forbidden");
    }
    const { error } = await context.supabase
      .from("card_assets")
      .update({
        status: "ready",
        deepzoom_path: data.deepzoomPath,
        ultra_path: data.ultraPath,
        preview_path: data.previewPath,
        thumb_path: data.thumbPath,
        ...(data.sourcePath ? { source_path: data.sourcePath } : {}),
        mime: data.mime,
        error: null,
        meta: data.meta as never,
        ready_at: new Date().toISOString(),
      })
      .eq("id", data.id)
      .eq("owner_id", context.userId);
    if (error) throw error;
    return { id: data.id, status: "ready" as CardAssetStatus };
  });

/** Record a processing failure. The raw upload stays usable for the listing. */
export const failCardAsset = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; error: string; sourcePath?: string; meta?: Record<string, unknown> }) => ({
    id: requireId(input?.id),
    error: String(input?.error ?? "Processing failed").slice(0, 500),
    sourcePath: input?.sourcePath ? safePath(input.sourcePath, "sourcePath") : null,
    meta: input?.meta && typeof input.meta === "object" ? input.meta : null,
  }))
  .handler(async ({ data, context }) => {
    if (data.sourcePath?.split("/")[0] !== context.userId) throw new Error("Forbidden");
    await context.supabase
      .from("card_assets")
      .update({
        status: "failed",
        error: data.error,
        ...(data.sourcePath ? { source_path: data.sourcePath } : {}),
        ...(data.meta ? { meta: data.meta as never } : {}),
      })
      .eq("id", data.id)
      .eq("owner_id", context.userId);
    return { id: data.id, status: "failed" as CardAssetStatus };
  });
